/**
 * ZEvaluation - Zod Validation Schemas for the AI Task Evaluation API
 *
 * Endpoints: /api/tools/ai-tasks/:taskId/evaluations/sessions + /api/tools/ai-tasks/evaluations/*
 * Controller: tools/ai-tasks/evaluation/core/infrastructure/controllers/evaluation.controller.ts
 *
 * The evaluation workbench lets an org build datasets against an AI task, run the
 * task over them (execute), score the outputs with an LLM judge (judge), and manage
 * reusable judge prompts. This is the INTERNAL evaluation domain, distinct from any
 * public-v1 evaluation surface.
 *
 * Enums here (`EvaluationSessionStatus`, `ExecutionStatus`, `JudgeStatus`,
 * `JudgePromptTemplate`, `JudgeModelProvider`) are evaluation-domain value objects,
 * NOT Prisma DbEnums, so they are declared as local `z.enum([...] as const)`.
 *
 * Entity timestamps are serialized to ISO strings at the controller boundary, so
 * every date field on the wire is `z.string()`.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// ENUMS (evaluation domain value objects — not Prisma DbEnums)
// ═══════════════════════════════════════════════════════════════

export const EVALUATION_SESSION_STATUSES = [
  "DRAFT",
  "EXECUTING",
  "EXECUTED",
  "JUDGING",
  "COMPLETED",
  "FAILED"
] as const;
const EvaluationSessionStatusSchema = z.enum(EVALUATION_SESSION_STATUSES);

export const EVALUATION_EXECUTION_STATUSES = ["PENDING", "RUNNING", "COMPLETED", "FAILED"] as const;
const ExecutionStatusSchema = z.enum(EVALUATION_EXECUTION_STATUSES);

export const EVALUATION_JUDGE_STATUSES = ["PENDING", "RUNNING", "COMPLETED", "FAILED"] as const;
const JudgeStatusSchema = z.enum(EVALUATION_JUDGE_STATUSES);

export const JUDGE_PROMPT_TEMPLATES = [
  "ACCURACY",
  "RELEVANCE",
  "QUALITY",
  "FORMAT_COMPLIANCE",
  "CUSTOM"
] as const;
const JudgePromptTemplateSchema = z.enum(JUDGE_PROMPT_TEMPLATES);

export const JUDGE_MODEL_PROVIDERS = ["ANTHROPIC", "OPEN_AI"] as const;
const JudgeModelProviderSchema = z.enum(JUDGE_MODEL_PROVIDERS);

// ═══════════════════════════════════════════════════════════════
// VALUE OBJECTS
// ═══════════════════════════════════════════════════════════════

/** Judge verdict for a single result — value is 0-10. */
const JudgeScoreSchema = z.object({
  value: z.number(),
  explanation: z.string(),
  modelUsed: z.string()
});

/**
 * How a session's outputs are scored. Lenient shape used when embedding a
 * persisted judge config in responses — response parsing must never reject on
 * length. The stricter request-body variant is `JudgeConfigBodySchema` below.
 */
const JudgeConfigSchema = z.object({
  modelProvider: JudgeModelProviderSchema,
  modelName: z.string(),
  promptTemplate: JudgePromptTemplateSchema,
  customPrompt: z.string().optional()
});

/** Request-body variant for the judge endpoint — mirrors the retired judge-config DTO. */
const JudgeConfigBodySchema = z.object({
  modelProvider: JudgeModelProviderSchema,
  modelName: z.string().min(1),
  promptTemplate: JudgePromptTemplateSchema,
  customPrompt: z.string().max(10000).optional()
});

// ═══════════════════════════════════════════════════════════════
// ENTITY RESPONSE SCHEMAS (dates as ISO strings)
// ═══════════════════════════════════════════════════════════════

const EvaluationSessionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  aiTaskId: z.string(),
  organizationId: z.string(),
  status: EvaluationSessionStatusSchema,
  judgeConfig: JudgeConfigSchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string().optional()
});
export type EvaluationSessionResponse = z.infer<typeof EvaluationSessionSchema>;

const DatasetRowSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  rowIndex: z.number(),
  input: z.unknown(),
  expectedOutput: z.unknown().optional(),
  createdAt: z.string()
});
export type DatasetRowResponse = z.infer<typeof DatasetRowSchema>;

const EvaluationResultSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  datasetRowId: z.string(),
  generatedOutput: z.unknown().optional(),
  executionStatus: ExecutionStatusSchema,
  executionError: z.string().optional(),
  executionTimeMs: z.number().optional(),
  judgeScore: JudgeScoreSchema.optional(),
  judgeStatus: JudgeStatusSchema,
  judgeError: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  // Embedded dataset row data for display (populated when fetching results)
  datasetRow: z
    .object({
      rowIndex: z.number(),
      input: z.unknown(),
      expectedOutput: z.unknown().optional()
    })
    .optional()
});
export type EvaluationResultResponse = z.infer<typeof EvaluationResultSchema>;

const SavedJudgePromptSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().optional(),
  prompt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().optional()
});
export type SavedJudgePromptResponse = z.infer<typeof SavedJudgePromptSchema>;

const ProgressSchema = z.object({
  completed: z.number(),
  failed: z.number(),
  total: z.number()
});

const SessionStatusSchema = z.object({
  session: EvaluationSessionSchema,
  executionProgress: ProgressSchema,
  judgingProgress: ProgressSchema,
  averageScore: z.number().nullable(),
  datasetRowCount: z.number()
});
export type SessionStatusResponse = z.infer<typeof SessionStatusSchema>;

const CloneSessionResultSchema = z.object({
  session: EvaluationSessionSchema,
  datasetRowCount: z.number(),
  resultCount: z.number()
});
export type CloneSessionResultResponse = z.infer<typeof CloneSessionResultSchema>;

const AvailableJudgeSchema = z.object({
  provider: JudgeModelProviderSchema,
  modelName: z.string(),
  displayName: z.string()
});

const PromptTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  prompt: z.string()
});

// ═══════════════════════════════════════════════════════════════
// SHARED WRAPPERS
// ═══════════════════════════════════════════════════════════════

const PaginationMetaSchema = z.object({
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  hasMore: z.boolean()
});

const PaginatedDatasetSchema = z.object({
  data: z.array(DatasetRowSchema),
  pagination: PaginationMetaSchema
});

const PaginatedResultsSchema = z.object({
  data: z.array(EvaluationResultSchema),
  pagination: PaginationMetaSchema
});

/** Async execute/judge acknowledgement — the actual work runs fire-and-forget. */
const AsyncStatusSchema = z.object({
  status: z.string(),
  message: z.string()
});

/** DELETE endpoints return a bare acknowledgement (no 204 — the envelope needs a body). */
const EvaluationOkSchema = z.object({
  success: z.literal(true)
});

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS
// ═══════════════════════════════════════════════════════════════

const TaskPathVarsSchema = z.object({ taskId: z.string() });
const SessionPathVarsSchema = z.object({ sessionId: z.string() });
const PromptPathVarsSchema = z.object({ promptId: z.string() });

const CreateSessionBodySchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(255),
  description: z.string().max(1000).optional()
});

const CloneSessionBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
  description: z.string().max(500).optional(),
  sourceSessionId: z.string().uuid()
});

const AddDatasetRowBodySchema = z.object({
  input: z.unknown(),
  expectedOutput: z.unknown().optional()
});

const CreateSavedPromptBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
  description: z.string().max(500).optional(),
  prompt: z.string().min(1).max(10000)
});

const UpdateSavedPromptBodySchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  description: z.string().max(500).optional().nullable(),
  prompt: z.string().min(1).max(10000).optional()
});

const TestPromptBodySchema = z.object({
  modelProvider: JudgeModelProviderSchema,
  modelName: z.string().min(1),
  prompt: z.string().min(1).max(10000),
  taskDescription: z.string(),
  outputFormat: z.string().optional(),
  input: z.unknown(),
  expectedOutput: z.unknown().optional(),
  generatedOutput: z.unknown()
});

const PaginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(500).optional(),
  offset: z.coerce.number().int().min(0).optional()
});

const IncrementalPaginationQuerySchema = PaginationQuerySchema.extend({
  updatedSince: z.string().optional()
});

// ═══════════════════════════════════════════════════════════════
// ZEVALUATION MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZEvaluation = {
  // ── Session Management ──────────────────────────────────────

  /** POST /api/tools/ai-tasks/:taskId/evaluations/sessions — create a session. */
  CreateSession: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/:taskId/evaluations/sessions",
    PathVars: TaskPathVarsSchema,
    Body: CreateSessionBodySchema,
    Response: ApiSuccessSchema(EvaluationSessionSchema)
  },

  /** POST /api/tools/ai-tasks/:taskId/evaluations/sessions/clone — clone a session with its data. */
  CloneSession: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/:taskId/evaluations/sessions/clone",
    PathVars: TaskPathVarsSchema,
    Body: CloneSessionBodySchema,
    Response: ApiSuccessSchema(CloneSessionResultSchema)
  },

  /** GET /api/tools/ai-tasks/:taskId/evaluations/sessions — list sessions for a task. */
  ListSessions: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/:taskId/evaluations/sessions",
    PathVars: TaskPathVarsSchema,
    Response: ApiSuccessSchema(z.array(EvaluationSessionSchema))
  },

  /** GET /api/tools/ai-tasks/evaluations/sessions/:sessionId — session status + progress. */
  GetSessionStatus: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/evaluations/sessions/:sessionId",
    PathVars: SessionPathVarsSchema,
    Response: ApiSuccessSchema(SessionStatusSchema)
  },

  /** DELETE /api/tools/ai-tasks/evaluations/sessions/:sessionId — delete a session. */
  DeleteSession: {
    method: "DELETE" as const,
    path: "/api/tools/ai-tasks/evaluations/sessions/:sessionId",
    PathVars: SessionPathVarsSchema,
    Response: ApiSuccessSchema(EvaluationOkSchema)
  },

  // ── Dataset Management ──────────────────────────────────────

  /** POST /api/tools/ai-tasks/evaluations/sessions/:sessionId/dataset/upload — upload a dataset file. */
  UploadDataset: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/evaluations/sessions/:sessionId/dataset/upload",
    PathVars: SessionPathVarsSchema,
    Body: z.custom<FormData>(),
    Response: ApiSuccessSchema(z.object({ rowCount: z.number() }))
  },

  /** GET /api/tools/ai-tasks/evaluations/sessions/:sessionId/dataset — paginated dataset rows. */
  GetDataset: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/evaluations/sessions/:sessionId/dataset",
    PathVars: SessionPathVarsSchema,
    Query: PaginationQuerySchema,
    Response: ApiSuccessSchema(PaginatedDatasetSchema)
  },

  /** POST /api/tools/ai-tasks/evaluations/sessions/:sessionId/dataset/rows — add one row manually. */
  AddDatasetRow: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/evaluations/sessions/:sessionId/dataset/rows",
    PathVars: SessionPathVarsSchema,
    Body: AddDatasetRowBodySchema,
    Response: ApiSuccessSchema(DatasetRowSchema)
  },

  // ── Execution ───────────────────────────────────────────────

  /** POST /api/tools/ai-tasks/evaluations/sessions/:sessionId/execute — run the task over the dataset (async). */
  ExecuteEvaluation: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/evaluations/sessions/:sessionId/execute",
    PathVars: SessionPathVarsSchema,
    Response: ApiSuccessSchema(AsyncStatusSchema)
  },

  // ── Judging ─────────────────────────────────────────────────

  /** POST /api/tools/ai-tasks/evaluations/sessions/:sessionId/judge — score results with an LLM judge (async). */
  JudgeResults: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/evaluations/sessions/:sessionId/judge",
    PathVars: SessionPathVarsSchema,
    Body: JudgeConfigBodySchema,
    Response: ApiSuccessSchema(AsyncStatusSchema)
  },

  // ── Results ─────────────────────────────────────────────────

  /** GET /api/tools/ai-tasks/evaluations/sessions/:sessionId/results — paginated results (incremental polling). */
  GetResults: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/evaluations/sessions/:sessionId/results",
    PathVars: SessionPathVarsSchema,
    Query: IncrementalPaginationQuerySchema,
    Response: ApiSuccessSchema(PaginatedResultsSchema)
  },

  // ── Configuration (static reference data) ───────────────────

  /** GET /api/tools/ai-tasks/evaluations/supported-formats — parser file extensions. */
  GetSupportedFormats: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/evaluations/supported-formats",
    Response: ApiSuccessSchema(z.object({ formats: z.array(z.string()) }))
  },

  /** GET /api/tools/ai-tasks/evaluations/available-judges — registered judge models. */
  GetAvailableJudges: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/evaluations/available-judges",
    Response: ApiSuccessSchema(z.object({ judges: z.array(AvailableJudgeSchema) }))
  },

  // ── Prompt Templates ────────────────────────────────────────

  /** GET /api/tools/ai-tasks/evaluations/prompt-templates — built-in judge prompt templates. */
  GetPromptTemplates: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/evaluations/prompt-templates",
    Response: ApiSuccessSchema(z.object({ templates: z.array(PromptTemplateSchema) }))
  },

  // ── Saved Prompts ───────────────────────────────────────────

  /** POST /api/tools/ai-tasks/evaluations/prompts — save a custom judge prompt. */
  CreateSavedPrompt: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/evaluations/prompts",
    Body: CreateSavedPromptBodySchema,
    Response: ApiSuccessSchema(SavedJudgePromptSchema)
  },

  /** GET /api/tools/ai-tasks/evaluations/prompts — list the org's saved judge prompts. */
  ListSavedPrompts: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/evaluations/prompts",
    Response: ApiSuccessSchema(z.array(SavedJudgePromptSchema))
  },

  /** GET /api/tools/ai-tasks/evaluations/prompts/:promptId — one saved judge prompt. */
  GetSavedPrompt: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/evaluations/prompts/:promptId",
    PathVars: PromptPathVarsSchema,
    Response: ApiSuccessSchema(SavedJudgePromptSchema)
  },

  /** PUT /api/tools/ai-tasks/evaluations/prompts/:promptId — update a saved judge prompt. */
  UpdateSavedPrompt: {
    method: "PUT" as const,
    path: "/api/tools/ai-tasks/evaluations/prompts/:promptId",
    PathVars: PromptPathVarsSchema,
    Body: UpdateSavedPromptBodySchema,
    Response: ApiSuccessSchema(SavedJudgePromptSchema)
  },

  /** DELETE /api/tools/ai-tasks/evaluations/prompts/:promptId — delete a saved judge prompt. */
  DeleteSavedPrompt: {
    method: "DELETE" as const,
    path: "/api/tools/ai-tasks/evaluations/prompts/:promptId",
    PathVars: PromptPathVarsSchema,
    Response: ApiSuccessSchema(EvaluationOkSchema)
  },

  // ── Test Prompt ─────────────────────────────────────────────

  /** POST /api/tools/ai-tasks/evaluations/prompts/test — one-off judge run against sample data. */
  TestPrompt: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/evaluations/prompts/test",
    Body: TestPromptBodySchema,
    Response: ApiSuccessSchema(z.object({ score: JudgeScoreSchema }))
  }
} as const;
